import React, { useEffect, useMemo, useState } from 'react';
import InvoiceArReportSetting from '../../components/admin/InvoiceArReportSetting';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, AlertTriangle } from 'lucide-react';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  getAgencyInsurances,
  createAgencyInsurance,
  updateAgencyInsurance,
  deleteAgencyInsurance,
  duplicateClinicInsurances,
  type AgencyInsuranceResponse,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import InsuranceModal from '../../components/admin/InsuranceModal';
import type {
  InsuranceForm,
  InsuranceModalTab,
} from '../../components/admin/InsuranceModal';
import { FeatureFlag } from '../../constants/featureFlags';
import {
  DEFAULT_BILL_TYPE,
  effectiveBillType,
  NO_READINESS_SKIPS,
  READINESS_SKIP_FIELDS,
  type ReadinessSkipFlags,
} from '../../constants/insuranceBilling';
import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { EnrollmentsTab } from '../../components/admin/EnrollmentsTab';
import {
  isMedicarePayorType,
  formatDuplicateInsurancePreview,
  previewDuplicateInsurances,
  isWellSkySyncedInsurance,
  payerLinkSaveNotice,
  type SavedPayerLink,
} from '../../components/admin/insuranceUtils';
import { clinicParticipatesInKinnser } from '../../utils/clinicKeyUtils';
import { ClinicSelect } from '../../components/clinic/ClinicSelect';

// The Roger-only billing settings, as every save sends them. The bill type goes
// only where its select shows.
const rogerBillingSettings = (
  form: InsuranceForm,
  canSetBillType: boolean
) => ({
  ...(Object.fromEntries(
    READINESS_SKIP_FIELDS.map(field => [field, form[field]])
  ) as ReadinessSkipFlags),
  ...(canSetBillType
    ? { bill_type: effectiveBillType(form.bill_type, form.payor_type) }
    : {}),
});

const DEFAULT_INSURANCE_FORM: InsuranceForm = {
  agency_clinic_id: '',
  name: '',
  edi_payer_id: '',
  edi_clearinghouse: '',
  visit_authorization_required: false,
  noa_required: false,
  noa_omit_admitting_diagnosis: null,
  include_location_codes_on_noa: null,
  payor_noa_omit_admitting_diagnosis: null,
  payor_noa_include_location_codes: null,
  kinnser_use_fips_code: null,
  ...NO_READINESS_SKIPS,
  bill_type: DEFAULT_BILL_TYPE,
  payor_type: null,
  billing_frequency: 'MONTHLY',
  pdgm_enabled: false,
  charge_type: 'VISIT',
  type_of_bill: null,
  do_not_multiply_charges_by_units: false,
  include_non_covered_visits_on_claims: false,
  health_plan_id: '',
  provider_code: '',
  provider_number: '',
  address_one: '',
  address_two: '',
  city: '',
  state: '',
  zip_code: '',
};

interface InsuranceSectionProps {
  clinics: AgencyClinicResponse[];
  isSuperUser: boolean;
  /** Always true today: Admin shows this section only at EMR agencies. */
  isRogerEmrEnabled: boolean;
  /** Incremented by the parent to force a reload (e.g. after a clinic info sync). */
  refreshToken?: number;
  /** Reports the needs-action count to Admin's tab badge. */
  onEnrollmentCountChange?: (count: number) => void;
  /** An insurance whose edit window a link (Create Claim's error) asked for. */
  requestedEditInsuranceId?: string | null;
  /** Called once that request is acted on, so a reload does not reopen it. */
  onEditInsuranceRequestHandled?: () => void;
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  clinics,
  isSuperUser,
  isRogerEmrEnabled,
  refreshToken = 0,
  onEnrollmentCountChange,
  requestedEditInsuranceId,
  onEditInsuranceRequestHandled,
}) => {
  const { canEdit } = useAdminSectionAccess('insurance');
  const {
    user,
    showSuccess,
    showError,
    addToast,
    hasFeatureFlag,
    fetchAgencyInsurances,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      addToast: state.addToast,
      hasFeatureFlag: state.hasFeatureFlag,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
    }))
  );

  const [insurances, setInsurances] = useState<AgencyInsuranceResponse[]>([]);
  const [hasLoadedInsurances, setHasLoadedInsurances] = useState(false);

  // Insurance create/edit modal state
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [insuranceModalMode, setInsuranceModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  // True when editing an insurance the WellSky sync still owns: modal is view-only.
  const [editingInsuranceSynced, setEditingInsuranceSynced] = useState(false);
  const [editingBillingLocked, setEditingBillingLocked] = useState(false);
  const [editingSavedPayerLink, setEditingSavedPayerLink] =
    useState<SavedPayerLink | null>(null);
  // Tab the insurance modal opens on: a table-row rates action deep-links
  // straight to the Billing rates tab.
  const [insuranceModalInitialTab, setInsuranceModalInitialTab] =
    useState<InsuranceModalTab>('details');
  const [insuranceForm, setInsuranceForm] = useState<InsuranceForm>(
    DEFAULT_INSURANCE_FORM
  );
  // This applies only to a single new-insurance session; edits retain their
  // persisted/synced flag behavior.
  const [hasManuallyChangedNoaOmit, setHasManuallyChangedNoaOmit] =
    useState(false);
  const [hasManuallyChangedNonCovered, setHasManuallyChangedNonCovered] =
    useState(false);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingInsuranceId, setDeletingInsuranceId] = useState<string | null>(
    null
  );
  const [isDeletingInsurance, setIsDeletingInsurance] = useState(false);

  // Duplicate-insurances modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSourceClinicId, setDuplicateSourceClinicId] = useState('');
  const [duplicateDestClinicId, setDuplicateDestClinicId] = useState('');
  const [isDuplicatingInsurances, setIsDuplicatingInsurances] = useState(false);

  const canSetBillType =
    isRogerEmrEnabled && hasFeatureFlag(FeatureFlag.INVOICE_BILLING);

  // Load insurances
  const loadInsurances = async () => {
    if (!user?.agency?.id) return;

    try {
      const data = await getAgencyInsurances(user.agency.id);
      setInsurances(data);
    } catch (error) {
      console.error('Failed to load insurances:', error);
      showError('Failed to load insurance data');
    } finally {
      setHasLoadedInsurances(true);
    }
  };

  // This page keeps insurances in local state, but the shared store caches
  // agencyInsurances for the whole session and never invalidates on its own.
  // Without refreshing it here, consumers like the intake payer select keep
  // stale names and settings until a hard page refresh.
  const refreshInsurancesAfterMutation = async () => {
    await Promise.all([loadInsurances(), fetchAgencyInsurances()]);
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadInsurances();
    }
  }, [user?.agency?.id, refreshToken]);

  // Open the requested insurance's edit window once the list has loaded. A
  // viewer who cannot edit, or an id not in this agency's list, gets the list.
  useEffect(() => {
    if (!requestedEditInsuranceId || !hasLoadedInsurances) return;
    const insurance = insurances.find(
      entry => entry.id === requestedEditInsuranceId
    );
    if (insurance && canEdit && isRogerEmrEnabled) {
      handleEditInsurance(insurance);
    }
    onEditInsuranceRequestHandled?.();
  }, [requestedEditInsuranceId, hasLoadedInsurances, insurances, canEdit]);

  // Manual insurance create: Roger-only clinics (not Kinnser-participating).
  // Kinnser-participating clinics manage payers via clinic-info sync.
  const rogerEmrClinics = clinics.filter(c => !clinicParticipatesInKinnser(c));
  const canAddInsurance = isRogerEmrEnabled && rogerEmrClinics.length > 0;
  // Why Add is off, in words that match the cause: an agency whose clinics
  // all sync from WellSky has clinics, just none Roger may add to.
  const addInsuranceBlockedReason = canAddInsurance
    ? null
    : clinics.length === 0
      ? 'Create a clinic first, then add its insurances.'
      : 'Insurances for WellSky clinics come from the WellSky sync. Add them in WellSky.';
  // Copy insurance/physicians only into non-Kinnser clinics (Kinnser payers
  // stay sync-owned). Need ≥1 Roger destination and ≥1 other clinic as source.
  const canCopyInsurancesToRogerClinic =
    clinics.length >= 2 && rogerEmrClinics.length >= 1;

  // Insurance modal handlers
  const handleCreateInsurance = () => {
    setInsuranceModalInitialTab('details');
    setInsuranceModalMode('create');
    setEditingInsuranceId(null);
    setEditingInsuranceSynced(false);
    setEditingBillingLocked(false);
    setEditingSavedPayerLink(null);
    setInsuranceForm(DEFAULT_INSURANCE_FORM);
    setHasManuallyChangedNoaOmit(false);
    setHasManuallyChangedNonCovered(false);
    setIsInsuranceModalOpen(true);
  };

  const handleEditInsurance = (
    insurance: AgencyInsuranceResponse,
    initialTab: InsuranceModalTab = 'details'
  ) => {
    setInsuranceModalInitialTab(initialTab);
    setInsuranceModalMode('edit');
    setEditingInsuranceId(insurance.id);
    setEditingInsuranceSynced(
      isWellSkySyncedInsurance(insurance, user?.agency?.is_kinnser_enabled)
    );
    setHasManuallyChangedNonCovered(false);
    setEditingBillingLocked(insurance.billing_settings_locked ?? false);
    setEditingSavedPayerLink({
      ediPayerId: insurance.edi_payer_id || '',
      linked: !!insurance.payor_id,
    });
    setInsuranceForm({
      agency_clinic_id: '',
      name: insurance.name,
      edi_payer_id: insurance.edi_payer_id || '',
      edi_clearinghouse: insurance.edi_clearinghouse || '',
      visit_authorization_required:
        insurance.visit_authorization_required ?? false,
      noa_required: insurance.noa_required ?? false,
      noa_omit_admitting_diagnosis:
        insurance.noa_omit_admitting_diagnosis ?? null,
      include_location_codes_on_noa:
        insurance.include_location_codes_on_noa ?? null,
      payor_noa_omit_admitting_diagnosis:
        insurance.payor_noa_omit_admitting_diagnosis ?? null,
      payor_noa_include_location_codes:
        insurance.payor_noa_include_location_codes ?? null,
      kinnser_use_fips_code: insurance.kinnser_use_fips_code ?? null,
      ...(Object.fromEntries(
        READINESS_SKIP_FIELDS.map(field => [field, insurance[field] ?? false])
      ) as ReadinessSkipFlags),
      bill_type: insurance.bill_type ?? DEFAULT_BILL_TYPE,
      payor_type: insurance.payor_type ?? null,
      billing_frequency: insurance.billing_frequency ?? 'MONTHLY',
      // PDGM follows the stored flag on any payer type; unset rows default to
      // On only for Medicare types.
      pdgm_enabled:
        insurance.pdgm_enabled ??
        isMedicarePayorType(insurance.payor_type ?? null),
      charge_type: insurance.charge_type ?? 'VISIT',
      type_of_bill: insurance.type_of_bill ?? null,
      do_not_multiply_charges_by_units:
        insurance.do_not_multiply_charges_by_units ?? false,
      include_non_covered_visits_on_claims:
        insurance.include_non_covered_visits_on_claims ?? false,
      health_plan_id: insurance.health_plan_id ?? '',
      provider_code: insurance.provider_code ?? '',
      provider_number: insurance.provider_number ?? '',
      address_one: insurance.address_one ?? '',
      address_two: insurance.address_two ?? '',
      city: insurance.city ?? '',
      state: insurance.state ?? '',
      zip_code: insurance.zip_code ?? '',
    });
    setIsInsuranceModalOpen(true);
  };

  const handleSaveInsurance = async () => {
    if (insuranceModalMode === 'create') {
      if (!insuranceForm.agency_clinic_id) {
        showError('Please select a clinic');
        return;
      }
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      if (!insuranceForm.payor_type) {
        showError('Please select a payer type');
        return;
      }
      // A new Roger-EMR insurance must carry an explicit FL 4 scheme.
      // Mirrors the modal's disabled Save so the rule still holds if the button
      // is bypassed. Only Roger-EMR agencies see the billing section, which is
      // where the field lives.
      if (isRogerEmrEnabled && !insuranceForm.type_of_bill) {
        showError('Please select a type of bill');
        return;
      }
      setIsSavingInsurance(true);
      try {
        const created = await createAgencyInsurance({
          agency_clinic_id: insuranceForm.agency_clinic_id,
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || undefined,
          edi_clearinghouse:
            insuranceForm.edi_clearinghouse.trim() || undefined,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
          noa_omit_admitting_diagnosis:
            insuranceForm.noa_omit_admitting_diagnosis,
          include_location_codes_on_noa:
            insuranceForm.include_location_codes_on_noa,
          ...rogerBillingSettings(insuranceForm, canSetBillType),
          payor_type: insuranceForm.payor_type!,
          billing_frequency: insuranceForm.billing_frequency,
          pdgm_enabled: insuranceForm.pdgm_enabled,
          charge_type: insuranceForm.charge_type,
          type_of_bill: insuranceForm.type_of_bill,
          do_not_multiply_charges_by_units:
            insuranceForm.do_not_multiply_charges_by_units,
          include_non_covered_visits_on_claims:
            insuranceForm.include_non_covered_visits_on_claims,
          health_plan_id: insuranceForm.health_plan_id.trim() || null,
          provider_code: insuranceForm.provider_code.trim() || null,
          provider_number: insuranceForm.provider_number.trim() || null,
          address_one: insuranceForm.address_one.trim() || null,
          address_two: insuranceForm.address_two.trim() || null,
          city: insuranceForm.city.trim() || null,
          state: insuranceForm.state.trim() || null,
          zip_code: insuranceForm.zip_code.trim() || null,
        });
        addToast(
          payerLinkSaveNotice({
            savedPayerId: insuranceForm.edi_payer_id.trim(),
            saved: created,
            wasLinked: false,
            successTitle: 'Insurance created successfully',
          })
        );
        setIsInsuranceModalOpen(false);
        await refreshInsurancesAfterMutation();
      } catch (error) {
        console.error('Failed to create insurance:', error);
        showError('Failed to create insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    } else {
      if (!editingInsuranceId) return;
      // A synced save sends only Roger-native flags, never the name, so the name
      // requirement must not gate it — a WellSky row with a blank scraped name
      // would otherwise be unable to save the flags at all.
      if (!editingInsuranceSynced && !insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      setIsSavingInsurance(true);
      try {
        let notice: ReturnType<typeof payerLinkSaveNotice> = {
          variant: 'default',
          title: 'Insurance updated successfully',
        };
        if (editingInsuranceSynced) {
          // WellSky-synced rows accept only the Roger-native NOA flags,
          // readiness skips and the non-covered setting. Every other field is owned by the clinic-info
          // sync, and echoing a name read when the modal opened would write a
          // stale one back if the sync renamed the row in the meantime.
          await updateAgencyInsurance(editingInsuranceId, {
            noa_omit_admitting_diagnosis:
              insuranceForm.noa_omit_admitting_diagnosis,
            include_location_codes_on_noa:
              insuranceForm.include_location_codes_on_noa,
            ...rogerBillingSettings(insuranceForm, canSetBillType),
            include_non_covered_visits_on_claims:
              insuranceForm.include_non_covered_visits_on_claims,
          });
        } else {
          const updated = await updateAgencyInsurance(editingInsuranceId, {
            name: insuranceForm.name.trim(),
            edi_payer_id: insuranceForm.edi_payer_id.trim() || null,
            edi_clearinghouse: insuranceForm.edi_clearinghouse.trim() || null,
            visit_authorization_required:
              insuranceForm.visit_authorization_required,
            noa_required: insuranceForm.noa_required,
            noa_omit_admitting_diagnosis:
              insuranceForm.noa_omit_admitting_diagnosis,
            include_location_codes_on_noa:
              insuranceForm.include_location_codes_on_noa,
            ...rogerBillingSettings(insuranceForm, canSetBillType),
            payor_type: insuranceForm.payor_type,
            billing_frequency: insuranceForm.billing_frequency,
            pdgm_enabled: insuranceForm.pdgm_enabled,
            charge_type: insuranceForm.charge_type,
            type_of_bill: insuranceForm.type_of_bill,
            do_not_multiply_charges_by_units:
              insuranceForm.do_not_multiply_charges_by_units,
            include_non_covered_visits_on_claims:
              insuranceForm.include_non_covered_visits_on_claims,
            health_plan_id: insuranceForm.health_plan_id.trim() || null,
            provider_code: insuranceForm.provider_code.trim() || null,
            provider_number: insuranceForm.provider_number.trim() || null,
            address_one: insuranceForm.address_one.trim() || null,
            address_two: insuranceForm.address_two.trim() || null,
            city: insuranceForm.city.trim() || null,
            state: insuranceForm.state.trim() || null,
            zip_code: insuranceForm.zip_code.trim() || null,
          });
          notice = payerLinkSaveNotice({
            savedPayerId: insuranceForm.edi_payer_id.trim(),
            saved: updated,
            wasLinked: editingSavedPayerLink?.linked ?? false,
            successTitle: notice.title,
          });
        }
        addToast(notice);
        setIsInsuranceModalOpen(false);
        await refreshInsurancesAfterMutation();
      } catch (error) {
        console.error('Failed to update insurance:', error);
        showError(
          getBusinessErrorMessage(error) ??
            'Failed to update insurance. Please try again.'
        );
      } finally {
        setIsSavingInsurance(false);
      }
    }
  };

  // Until the user sets the switch this session: a new insurance follows its
  // payer type, and an edited one turns on when it becomes Traditional Medicare,
  // as the server does (it never turns off on edit).
  const nonCoveredDefaultFor = (
    prev: InsuranceForm,
    updates: Partial<InsuranceForm>
  ): boolean => {
    const kept =
      updates.include_non_covered_visits_on_claims ??
      prev.include_non_covered_visits_on_claims;
    if (
      hasManuallyChangedNonCovered ||
      updates.payor_type === undefined ||
      editingBillingLocked
    ) {
      return kept;
    }
    if (insuranceModalMode === 'create') {
      return updates.payor_type === 'MEDICARE_TRADITIONAL';
    }
    const becomesTraditional =
      updates.payor_type === 'MEDICARE_TRADITIONAL' &&
      prev.payor_type !== 'MEDICARE_TRADITIONAL';
    return becomesTraditional || kept;
  };

  const handleInsuranceFormChange = (updates: Partial<InsuranceForm>) => {
    const manuallyChangedNoaOmit =
      insuranceModalMode === 'create' &&
      updates.noa_omit_admitting_diagnosis !== undefined;
    if (manuallyChangedNoaOmit) {
      setHasManuallyChangedNoaOmit(true);
    }
    if (updates.include_non_covered_visits_on_claims !== undefined) {
      setHasManuallyChangedNonCovered(true);
    }

    setInsuranceForm(prev => ({
      ...prev,
      ...updates,
      // Default this on every payer-type change only until the
      // user explicitly chooses the switch value in the current create session.
      noa_omit_admitting_diagnosis:
        insuranceModalMode === 'create' &&
        !hasManuallyChangedNoaOmit &&
        updates.payor_type !== undefined
          ? updates.payor_type === 'MEDICARE_TRADITIONAL'
            ? true
            : null
          : updates.noa_omit_admitting_diagnosis !== undefined
            ? updates.noa_omit_admitting_diagnosis
            : prev.noa_omit_admitting_diagnosis,
      include_non_covered_visits_on_claims: nonCoveredDefaultFor(prev, updates),
    }));
  };

  const handleCancelInsuranceModal = () => {
    setIsInsuranceModalOpen(false);
    setEditingInsuranceId(null);
    setInsuranceForm(DEFAULT_INSURANCE_FORM);
    setHasManuallyChangedNoaOmit(false);
    setHasManuallyChangedNonCovered(false);
  };

  const handleDeleteInsurance = (insuranceId: string) => {
    setDeletingInsuranceId(insuranceId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInsurance = async () => {
    if (!deletingInsuranceId) return;

    setIsDeletingInsurance(true);
    try {
      await deleteAgencyInsurance(deletingInsuranceId);
      showSuccess('Insurance retired');
      setIsDeleteDialogOpen(false);
      setDeletingInsuranceId(null);
      await refreshInsurancesAfterMutation();
    } catch (error) {
      console.error('Failed to retire insurance:', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to retire insurance. Please try again.';
      showError(message);
    } finally {
      setIsDeletingInsurance(false);
    }
  };

  const duplicateInsurancePreview = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return null;
    }
    return previewDuplicateInsurances(
      insurances,
      duplicateSourceClinicId,
      duplicateDestClinicId
    );
  }, [duplicateDestClinicId, duplicateSourceClinicId, insurances]);

  const duplicateInsurancePreviewText = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return 'Source and destination clinics must be different.';
    }
    if (!duplicateInsurancePreview) {
      return null;
    }
    return formatDuplicateInsurancePreview(duplicateInsurancePreview);
  }, [
    duplicateDestClinicId,
    duplicateInsurancePreview,
    duplicateSourceClinicId,
  ]);

  const handleOpenDuplicateInsurances = () => {
    setDuplicateSourceClinicId('');
    setDuplicateDestClinicId('');
    setIsDuplicateModalOpen(true);
  };

  const handleConfirmDuplicateInsurances = async () => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      showError('Please select both a source and destination clinic');
      return;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      showError('Source and destination clinics must be different');
      return;
    }
    setIsDuplicatingInsurances(true);
    try {
      const result = await duplicateClinicInsurances(
        duplicateSourceClinicId,
        duplicateDestClinicId
      );
      const copiedRates = result.copied_visit_rate_count ?? 0;
      showSuccess(
        `Copied ${result.created_count} insurance${
          result.created_count === 1 ? '' : 's'
        }` +
          (result.skipped_count > 0
            ? ` (${result.skipped_count} skipped — matching payer ID and name already present)`
            : '') +
          (copiedRates > 0
            ? `, including ${copiedRates} visit rate${
                copiedRates === 1 ? '' : 's'
              }`
            : '')
      );
      setIsDuplicateModalOpen(false);
      await loadInsurances();
    } catch (error) {
      console.error('Failed to copy insurances:', error);
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to copy insurances. Please try again.'
      );
    } finally {
      setIsDuplicatingInsurances(false);
    }
  };

  return (
    <>
      {canSetBillType && <InvoiceArReportSetting />}
      <EnrollmentsTab
        canRefresh={canEdit}
        onCountChange={onEnrollmentCountChange}
        insurances={insurances}
        agencyClinics={clinics}
        onAddInsurance={
          isRogerEmrEnabled && canEdit ? handleCreateInsurance : undefined
        }
        addInsuranceBlockedReason={addInsuranceBlockedReason}
        onCopyInsurances={
          isRogerEmrEnabled &&
          canEdit &&
          isSuperUser &&
          canCopyInsurancesToRogerClinic
            ? handleOpenDuplicateInsurances
            : undefined
        }
        onEdit={
          isRogerEmrEnabled && canEdit
            ? insurance => handleEditInsurance(insurance, 'details')
            : undefined
        }
        onEditRates={
          isRogerEmrEnabled && canEdit
            ? insurance => handleEditInsurance(insurance, 'rates')
            : undefined
        }
      />

      {/* Insurance Create/Edit Modal */}
      {isRogerEmrEnabled && (
        <InsuranceModal
          open={isInsuranceModalOpen}
          mode={insuranceModalMode}
          form={insuranceForm}
          onChange={handleInsuranceFormChange}
          onSave={handleSaveInsurance}
          onCancel={handleCancelInsuranceModal}
          clinics={rogerEmrClinics}
          saving={isSavingInsurance}
          wellSkySynced={editingInsuranceSynced}
          showBillType={canSetBillType}
          billingSettingsLocked={editingBillingLocked}
          insuranceId={editingInsuranceId}
          initialTab={insuranceModalInitialTab}
          isRogerEmrEnabled={isRogerEmrEnabled}
          singleTab
          savedPayerLink={editingSavedPayerLink}
          onDelete={
            canEdit && editingInsuranceId
              ? () => {
                  // Closed first, so the confirmation is the only dialog up.
                  setIsInsuranceModalOpen(false);
                  handleDeleteInsurance(editingInsuranceId);
                }
              : undefined
          }
        />
      )}

      {/* Duplicate Insurances Modal */}
      {isRogerEmrEnabled && isSuperUser && (
        <Dialog
          open={isDuplicateModalOpen}
          onOpenChange={setIsDuplicateModalOpen}
        >
          <DialogContent className='sm:max-w-[480px]'>
            <DialogHeader>
              <DialogTitle>Copy Insurances to Clinic</DialogTitle>
              <DialogDescription>
                Copy every insurance from one clinic onto a Roger-only clinic
                (not Kinnser-linked).
              </DialogDescription>
            </DialogHeader>
            <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
              <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5' />
              <span>
                Creates Roger-managed insurance copies, not WellSky-synced rows.
                Also copies visit rates onto new insurances, and onto existing
                matches that have no rates yet. Skips creating insurances that
                already exist on the destination with the same payer ID and
                name.
              </span>
            </div>
            <div className='space-y-4 py-2'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>From clinic</label>
                <ClinicSelect
                  clinics={clinics}
                  value={duplicateSourceClinicId}
                  onChange={e => setDuplicateSourceClinicId(e.target.value)}
                  placeholder='Select source clinic'
                />
              </div>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>To clinic</label>
                <ClinicSelect
                  clinics={rogerEmrClinics.filter(
                    c => c.id !== duplicateSourceClinicId
                  )}
                  value={duplicateDestClinicId}
                  onChange={e => setDuplicateDestClinicId(e.target.value)}
                  placeholder='Select destination clinic'
                />
              </div>
              {duplicateInsurancePreviewText && (
                <p className='text-sm text-muted-foreground'>
                  {duplicateInsurancePreviewText}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsDuplicateModalOpen(false)}
                disabled={isDuplicatingInsurances}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDuplicateInsurances}
                disabled={
                  isDuplicatingInsurances ||
                  !duplicateSourceClinicId ||
                  !duplicateDestClinicId ||
                  duplicateInsurancePreview?.createdCount === 0
                }
              >
                {isDuplicatingInsurances && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Copy Insurances
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Insurance Retire Confirmation Dialog */}
      {isRogerEmrEnabled && (
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className='sm:max-w-[400px]'>
            <DialogHeader>
              <DialogTitle>Retire Insurance</DialogTitle>
              <DialogDescription>
                Retire this insurance provider? It will no longer be offered for
                new patients. It can only be retired while no patient policy,
                episode, billing cycle or authorization uses it.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingInsuranceId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={confirmDeleteInsurance}
                disabled={isDeletingInsurance}
              >
                {isDeletingInsurance && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Retire
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default InsuranceSection;
